import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@node-space/hooks'
import { TextAction } from '@node-space/storybook-components'
import Box, { Box as Wrapper } from '@node-space/storybook-components/dist/Box'
import DoubleLabelSelect from '@node-space/storybook-components/dist/DoubleLabelSelect'
import Icon from '@node-space/storybook-components/dist/Icon'
import { MassPayoutsIcon } from '@node-space/storybook-components/dist/Icons'
import { StatusCard } from '@node-space/storybook-components/dist/StatusCard'
import { Text } from '@node-space/storybook-components/dist/Text'
import { useWalletsQuery } from 'hooks/queries/useWalletsQuery'
import { WalletType } from 'types/types'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'
import { formatString, roundNumberWithCommas } from 'utils/utils'
import { MassPayoutFileUpload } from './components/MassPayoutFileUpload'

export const MassPayouts = () => {
  const { t } = useTranslation()
  const { enableFiatCurrencyFilter } = useFeatureFlags()
  const [selectedWallet, setSelectedWallet] = useState<WalletType>()
  const [sourceWallets, setSourceWallets] = useState([])
  const supportedWithdrawCurrencies = enableFiatCurrencyFilter?.supportedWithdrawCurrencies
  const {
    data: wallets = [],
    isFetching: isFetchingWallets,
    isError: isWalletsFetchingError,
  } = useWalletsQuery()

  useEffect(() => {
    track.Amp.track(AmplitudeEvent.MASS_PAYOUTS, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
    })
  }, [])

  useEffect(() => {
    if (wallets?.length) {
      const sourceWallets = wallets
        ?.filter(
          wallet =>
            wallet?.currency?.code &&
            wallet?.currency?.fiat &&
            ((!supportedWithdrawCurrencies && wallet?.currency?.supportsWithdrawals) ||
              supportedWithdrawCurrencies?.includes(wallet?.currency?.code?.toUpperCase()))
        )
        .map(wallet => ({
          icon: wallet?.currency?.code?.toLowerCase(),
          walletIcon: true,
          value: wallet?.id?.toString(),
          label: wallet?.description,
          secondLabel: formatString(
            t('balanceWithCurrency'),
            roundNumberWithCommas(wallet?.balance, 2),
            wallet?.currency?.code
          ),
        }))

      setSourceWallets(sourceWallets)
    }
  }, [wallets])

  const onWalletChangeHandler = (walletId: string) => {
    const wallet = wallets?.find(wallet => wallet?.id?.toString() === walletId)
    setSelectedWallet(wallet)
  }

  return (
    <Wrapper flex alignItems="center" direction="col">
      <Box
        flex
        alignItems="center"
        className="col-span-1 xs:col-span-2"
        direction="col"
        flexSize="keep"
        justifyContent="center"
        maxWidth={512}
        tag="form"
        width="full"
      >
        <StatusCard
          borderless
          headline={t('massPayouts.massPayouts')}
          testid="mass-payouts-header-card"
          customIcon={<MassPayoutsIcon />}
          description={
            <Box
              id="mass-payouts-header-card-description"
              testid="mass-payouts-header-card-description"
            >
              <Text size="sm">{t('massPayouts.descriptionPartOne')}</Text>
              <Text size="sm">
                <TextAction text={t('massPayouts.downloadOurCsvTemplate')} />{' '}
                {t('massPayouts.enterRequiredData')}{' '}
                <TextAction text={t('massPayouts.learnMore')} />
              </Text>
            </Box>
          }
        />

        <Box alignItems="center" justifyContent="center" paddingB={20} width="full">
          <DoubleLabelSelect
            showOverflow
            id="mass-payouts-wallet-select"
            iconsUrl={process.env.ICONS_URL}
            name="massPayoutsWalletSelect"
            label={t('massPayouts.selectWallet')}
            menuMaxHeight={250}
            loading={isFetchingWallets}
            placeholder={t('searchOrSelectAWallet')}
            options={sourceWallets}
            value={selectedWallet?.id?.toString()}
            isSearchable={true}
            iconLeft={!selectedWallet?.id?.toString() && <Icon name="SearchIcon" />}
            onChange={onWalletChangeHandler}
            error={!isFetchingWallets && isWalletsFetchingError}
            errorText={t('fetchingWalletsError')}
          />
        </Box>

        {selectedWallet && <MassPayoutFileUpload />}
      </Box>
    </Wrapper>
  )
}
